import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import type { ItemMasterDto, ApiError } from "@procureflow/shared-types";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fetchWithAuth } from "@/lib/auth-client";
import { rowActivation } from "@/lib/row-navigation";
import { formatDate } from "@/lib/format";
import { formatUnitPrice, parseUnitPriceInput } from "@/lib/item-master-format";

// US-059 — Item Master catalogue management. Searchable, paginated list of
// catalogue items; Add/Edit form (item_code unique, item_name, uom, optional
// unit price in decimal dollars → stored as cents); Delete is confirmed and
// blocked by the API while any line item references the item.

export const Route = createFileRoute("/item-master")({
  head: () => ({
    meta: [
      { title: "Item Master — ProcureFlow" },
      { name: "description", content: "Manage the catalogue of standard items." },
    ],
  }),
  component: ItemMasterPage,
});

const PAGE_SIZE = 20;

type FetchState =
  | { kind: "loading" }
  | { kind: "loaded"; items: ItemMasterDto[]; total: number }
  | { kind: "forbidden" }
  | { kind: "error"; message: string };

interface FormState {
  open: boolean;
  editing: ItemMasterDto | null;
  item_code: string;
  item_name: string;
  uom: string;
  unit_price: string;
  error: string | null;
  saving: boolean;
}

const EMPTY_FORM: FormState = {
  open: false,
  editing: null,
  item_code: "",
  item_name: "",
  uom: "",
  unit_price: "",
  error: null,
  saving: false,
};

function ItemMasterPage() {
  const [state, setState] = useState<FetchState>({ kind: "loading" });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [deleting, setDeleting] = useState<ItemMasterDto | null>(null);

  async function load(currentSearch: string, currentPage: number) {
    if (import.meta.env.DEV) {
      const { mswReady } = await import("@/mocks/browser");
      await mswReady;
    }
    try {
      const params = new URLSearchParams({ page: String(currentPage), limit: String(PAGE_SIZE) });
      if (currentSearch.trim()) params.set("search", currentSearch.trim());
      const res = await fetchWithAuth(`/api/item-master?${params.toString()}`);
      if (res.status === 403) {
        setState({ kind: "forbidden" });
        return;
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as Partial<ApiError>;
        setState({ kind: "error", message: body.message ?? "Failed to load items." });
        return;
      }
      const { data, meta } = (await res.json()) as {
        data: ItemMasterDto[];
        meta: { total: number };
      };
      setState({ kind: "loaded", items: data, total: meta.total });
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : "Unexpected error.",
      });
    }
  }

  // Debounce search; reset to page 1 whenever the query changes.
  useEffect(() => {
    const handle = setTimeout(() => void load(search, page), search ? 250 : 0);
    return () => clearTimeout(handle);
  }, [search, page]);

  function openCreate() {
    setForm({ ...EMPTY_FORM, open: true });
  }

  function openEdit(item: ItemMasterDto) {
    setForm({
      open: true,
      editing: item,
      item_code: item.item_code,
      item_name: item.item_name,
      uom: item.uom,
      unit_price: item.unit_price_minor === null ? "" : (item.unit_price_minor / 100).toFixed(2),
      error: null,
      saving: false,
    });
  }

  async function submitForm() {
    const item_code = form.item_code.trim();
    const item_name = form.item_name.trim();
    const uom = form.uom.trim();
    if (!item_code || !item_name || !uom) {
      setForm((f) => ({ ...f, error: "Item code, name, and unit of measure are required." }));
      return;
    }
    const price = parseUnitPriceInput(form.unit_price);
    if (price === "invalid") {
      setForm((f) => ({ ...f, error: "Enter a valid non-negative price, or leave it blank." }));
      return;
    }
    setForm((f) => ({ ...f, saving: true, error: null }));

    const payload = { item_code, item_name, uom, unit_price_minor: price };
    const editing = form.editing;
    const res = await fetchWithAuth(
      editing ? `/api/item-master/${editing.id}` : "/api/item-master",
      {
        method: editing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );

    if (res.ok) {
      toast.success(editing ? "Item updated." : "Item created.");
      setForm(EMPTY_FORM);
      void load(search, page);
      return;
    }
    const body = (await res.json().catch(() => ({}))) as Partial<ApiError>;
    if (body.code === "DUPLICATE_ITEM_CODE") {
      setForm((f) => ({ ...f, saving: false, error: "An item with this code already exists." }));
      return;
    }
    setForm((f) => ({ ...f, saving: false, error: body.message ?? "Failed to save item." }));
  }

  async function confirmDelete() {
    if (!deleting) return;
    const target = deleting;
    setDeleting(null);
    const res = await fetchWithAuth(`/api/item-master/${target.id}`, { method: "DELETE" });
    if (res.status === 204) {
      toast.success("Item deleted.");
      void load(search, page);
      return;
    }
    const body = (await res.json().catch(() => ({}))) as Partial<ApiError> & {
      details?: { referencing_count?: number };
    };
    if (body.code === "ITEM_IN_USE") {
      const count = body.details?.referencing_count ?? 0;
      toast.error(
        `Cannot delete “${target.item_code}” — it is referenced by ${count} line item${
          count === 1 ? "" : "s"
        }.`,
      );
      return;
    }
    toast.error(body.message ?? "Failed to delete item.");
  }

  const totalPages = state.kind === "loaded" ? Math.max(1, Math.ceil(state.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-6" data-testid="item-master-section">
      <PageHeader
        title="Item Master"
        description="Manage the catalogue of standard items used across purchasing documents."
        action={
          <Button onClick={openCreate} data-testid="add-item-button">
            <Plus className="mr-2 h-4 w-4" />
            Add item
          </Button>
        }
      />

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Search by code or name…"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          data-testid="item-search"
        />
      </div>

      {state.kind === "forbidden" ? (
        <div
          className="rounded-2xl bg-card border border-border p-10 text-sm text-muted-foreground shadow-elegant"
          data-testid="item-master-forbidden"
        >
          You don't have access to the item master.
        </div>
      ) : state.kind === "error" ? (
        <div className="rounded-2xl bg-card border border-border p-10 text-sm text-destructive shadow-elegant">
          {state.message}
        </div>
      ) : (
        <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item code</TableHead>
                <TableHead>Item name</TableHead>
                <TableHead>UOM</TableHead>
                <TableHead className="text-right">Unit price</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Manage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {state.kind === "loading" ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center text-sm text-muted-foreground py-10"
                  >
                    Loading…
                  </TableCell>
                </TableRow>
              ) : state.items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center text-sm text-muted-foreground py-10"
                    data-testid="item-master-empty"
                  >
                    No items found.
                  </TableCell>
                </TableRow>
              ) : (
                state.items.map((item) => (
                  <TableRow
                    key={item.id}
                    data-testid={`item-row-${item.id}`}
                    className="cursor-pointer hover:bg-muted/30 transition-colors"
                    {...rowActivation(() => openEdit(item))}
                  >
                    <TableCell className="font-medium">{item.item_code}</TableCell>
                    <TableCell>{item.item_name}</TableCell>
                    <TableCell>{item.uom}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatUnitPrice(item.unit_price_minor)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(item.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(item);
                          }}
                          data-testid={`edit-item-${item.id}`}
                          aria-label={`Edit ${item.item_code}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleting(item);
                          }}
                          data-testid={`delete-item-${item.id}`}
                          aria-label={`Delete ${item.item_code}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {state.kind === "loaded" && state.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {page} of {totalPages} · {state.total} items
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="item-prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              data-testid="item-next-page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Add / Edit form */}
      <Dialog open={form.open} onOpenChange={(open) => !open && setForm(EMPTY_FORM)}>
        <DialogContent dismissable={false}>
          <DialogHeader>
            <DialogTitle>{form.editing ? "Edit item" : "Add item"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="im-code">Item code</Label>
              <Input
                id="im-code"
                value={form.item_code}
                maxLength={32}
                onChange={(e) => setForm((f) => ({ ...f, item_code: e.target.value, error: null }))}
                data-testid="item-code-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="im-name">Item name</Label>
              <Input
                id="im-name"
                value={form.item_name}
                maxLength={255}
                onChange={(e) => setForm((f) => ({ ...f, item_name: e.target.value, error: null }))}
                data-testid="item-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="im-uom">Unit of measure</Label>
                <Input
                  id="im-uom"
                  value={form.uom}
                  maxLength={32}
                  placeholder="pcs, kg, box…"
                  onChange={(e) => setForm((f) => ({ ...f, uom: e.target.value, error: null }))}
                  data-testid="item-uom-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="im-price">Unit price (optional)</Label>
                <Input
                  id="im-price"
                  value={form.unit_price}
                  inputMode="decimal"
                  placeholder="0.00"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, unit_price: e.target.value, error: null }))
                  }
                  data-testid="item-price-input"
                />
              </div>
            </div>
            {form.error && (
              <p className="text-sm text-destructive" data-testid="item-form-error">
                {form.error}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(EMPTY_FORM)} disabled={form.saving}>
              Cancel
            </Button>
            <Button onClick={submitForm} disabled={form.saving} data-testid="save-item-button">
              {form.saving ? "Saving…" : form.editing ? "Save changes" : "Create item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.item_code}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the item from the catalogue. Items referenced by existing
              line items cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-item">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
